import axios, { CancelToken } from 'axios';
import { CarrierTypeEnum, ShippingSplitPayload } from 'domains/shippingDomain';
import {
  FileItem,
  JobNotesItem,
  JobPoItem,
  JobPoView,
  OrgMembers,
  ProofItem,
  ProofMsg,
  SimpleOrg,
  Summary,
  WorkspaceTabShippingBillingDTO,
  WorkspaceTabSummaryDTO,
} from 'domains/workspaceDetailDomain';
import { JobItem } from 'domains/workspaceDomain';
import api from 'utils/api';

const workspaceDetailService = {
  getSummary: async (jobId: number) => {
    return api.get<Summary>(`/api/jobs/${jobId}/summary`);
  },
  getAllFile: async (jobId: number) => {
    return api.get<FileItem[]>(`/api/jobs/files/list/all/${jobId}`);
  },
  saveFile: async (fileItem: Partial<FileItem>) => {
    return api.post<FileItem>(`/api/jobs/files`, { ...fileItem });
  },
  deleteFile: async (fileItem: Partial<FileItem>) => {
    return api.delete<string>(`/api/jobs/files/${fileItem.id}`);
  },
  uploadFileToJob: async (
    s3Url: string,
    file: File,
    cancelTokenObject: { token: CancelToken },
    jobId: number | string,
    onUploadProgress?: (param: ProgressEvent) => void
  ) => {
    try {
      await axios.put(s3Url, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': `attachment; filename=${encodeURIComponent(
            file.name
          )}`,
        },
        cancelToken: cancelTokenObject.token,
        onUploadProgress,
      });
      await workspaceDetailService.saveFile({
        fileName: file.name,
        fileUrl: new URL(s3Url).pathname.replace(/^\//, ''),
        jobId: Number(jobId),
      });
    } catch (e) {}
  },
  uploadFileToWorkSpace: async (
    s3Url: string,
    file: File,
    cancelTokenObject?: { token: CancelToken },
    onUploadProgress?: (param: ProgressEvent) => void
  ) => {
    try {
      return await axios.put(s3Url, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': `attachment; filename=${encodeURIComponent(
            file.name
          )}`,
        },
        cancelToken: cancelTokenObject?.token,
        onUploadProgress,
      });
    } catch {}
  },
  fileSubmit: async (job: JobItem) => {
    return api.put(`/api/jobs/files/submit/${job.id}`);
  },
  getAllProofFile: async (proofItem: ProofItem) => {
    return api.get<FileItem[]>(
      `/api/jobs/proof-items/list/all/${proofItem.id}`
    );
  },
  saveProofFile: async (fileItem: Partial<FileItem>) => {
    return api.post<FileItem>(`/api/jobs/proof-items`, { ...fileItem });
  },
  deleteProofFile: async (fileItem: Partial<FileItem>) => {
    return api.delete<string>(`/api/jobs/proof-items/${fileItem.id}`);
  },
  getProofAll: async (job: JobItem) => {
    return api.get<ProofItem[]>(`/api/jobs/proofs/list/all/${job.id}`);
  },
  nextProofVersion: async (proofItem: ProofItem) => {
    return api.put(
      `/api/jobs/proofs/next-version/${proofItem.jobId}/${proofItem.proofType}`
    );
  },
  shippingNumber: async (proofItem: ProofItem) => {
    return api.put(
      `/api/jobs/proofs/shipping/${proofItem.id}/${proofItem.trackingCode}/UPS`
    );
  },
  proofMsg: async (proofItem: ProofMsg) => {
    return api.post(`/api/jobs/proofs/${proofItem.jobProofId}/msg`, {
      msg: proofItem.msg,
    });
  },
  updateProofMsg: async (proofItem: ProofMsg) => {
    return api.put(
      `/api/jobs/proofs/${proofItem.jobProofId}/msg/${proofItem.id}`,
      {
        msg: proofItem.msg,
      }
    );
  },
  supplierApproval: async (proofItem: ProofItem) => {
    return api.put(`/api/jobs/proofs/request-approval/${proofItem.id}`);
  },
  buyerApprove: async (proofItem: ProofItem) => {
    return api.put(`/api/jobs/proofs/approve/${proofItem.id}`);
  },
  buyerDecline: async (proofItem: ProofItem) => {
    return api.put(`/api/jobs/proofs/decline/${proofItem.id}`);
  },
  saveNote: async (payload: JobNotesItem) => {
    return api.post(`/api/jobs/${payload.jobId}/notes`, { ...payload });
  },
  getNotes: async (payload: JobItem) => {
    return api.get<JobNotesItem[]>(`/api/jobs/${payload.id}/notes/list`);
  },
  poAccept: async (payload: JobItem) => {
    return api.put(`/api/jobs/po/accept/${payload.id}`, { jobId: payload.id });
  },
  poDecline: async (payload: JobItem) => {
    return api.put(`/api/jobs/po/decline/${payload.id}`, { jobId: payload.id });
  },
  poSend: async (payload: JobPoItem) => {
    return api.put(`/api/jobs/po/send`, { ...payload });
  },
  poView: async (payload: JobItem) => {
    return api.get<JobPoView>(`/api/jobs/po/view/${payload.id}`);
  },
  shippingCarriers: async () => {
    return api.get<CarrierTypeEnum[]>(`/api/shipping/carriers`);
  },
  splitShipping: async (payload: Partial<ShippingSplitPayload>) => {
    return api.put(
      `/api/jobs/${payload.jobId}/shipping/update`,
      payload.shippingItems
    );
  },
  batchShippingUpdate: async (payload: ShippingSplitPayload[]) => {
    return api.put(`/api/jobs/batch/shipping/update`, { jobItems: payload });
  },
  orgMembers: async (payload: number) => {
    return api.get<OrgMembers[]>(`/api/networks/org-members/${payload}`);
  },
  compareWithOrg: async (payload: any) => {
    return api.get(`/api/job-config/compare-with-org/${payload.id}`, {
      ...payload,
    });
  },
  getSimpleOrgList: async (networkId: number) => {
    return api.get<SimpleOrg[]>(`/networks/${networkId}/view-as-simple-org`);
  },
  getMarketplaceOrgList: async (networkId: number) => {
    return api.get<SimpleOrg[]>(
      `/networks/${networkId}/view-marketplace-network-org`
    );
  },
  addNoEligibleOrg: async (rfqId: number, orgId: number) => {
    return api.post(
      `/job-config/job-rfq/${rfqId}/no-eligible-org/add?orgId=${orgId}`
    );
  },
  getWorkspaceSummary: async (jobId: number) => {
    return api.get<WorkspaceTabSummaryDTO>(`/workspace/${jobId}/summary`);
  },
  getWorkspaceShippingBilling: async (jobId: number) => {
    return api.get<WorkspaceTabShippingBillingDTO>(
      `/workspace/${jobId}/shipping-billing`
    );
  },
};

export default workspaceDetailService;
